// Reduces error-shaped values in a monitoring meta object to a safe summary so
// raw axios errors never reach the logging sink — their `config.headers` carry
// the Authorization bearer, and `request`/`response` can carry PII.

interface ErrorLike {
  message?: unknown;
  response?: { status?: unknown; data?: { code?: unknown } };
}

/** The safe summary of a failed request. */
export interface ErrorSummary {
  message?: string;
  status?: number;
  code?: string;
}

const isErrorLike = (value: unknown): value is ErrorLike =>
  value instanceof Error ||
  (value != null &&
    typeof value === 'object' &&
    ('isAxiosError' in value || 'response' in value || 'stack' in value));

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' ? value : undefined;

// The single definition of which fields of a failed request are safe to send
// to a monitoring sink. Exported so callers that build their own payload
// (see `shared/api/query-errors.ts`) do not re-derive it.
export const summarizeError = (error: unknown): ErrorSummary => {
  const { message, response } = (error ?? {}) as ErrorLike;
  return {
    message: asString(message),
    status: asNumber(response?.status),
    code: asString(response?.data?.code),
  };
};

export function sanitizeLogMeta<T>(meta: T): T {
  if (meta == null || typeof meta !== 'object') return meta;
  return Object.fromEntries(
    Object.entries(meta).map(([key, value]) => [
      key,
      isErrorLike(value) ? summarizeError(value) : value,
    ]),
  ) as T;
}
